import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, API_BASE } from '../lib/api';
import {
  PageTitle, Card, Section, Pill, Kpi, Btn, FairnessPill, ScoreChip, EmptyState, fmt,
} from '../components/ui-kit';
import { ScanText } from 'lucide-react';

interface BlindCandidate {
  blind_id: string;
  wolf_score?: number;
  wolf_tier?: string;
  fairness_grade?: string;
  title?: string;
  seniority?: string;
  years_experience?: number;
  city_zone?: string;
  available_from?: string;
  skills_top5?: string[];
  certifications?: string[];
  domains?: string[];
}

export default function Marketplace() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<BlindCandidate[] | null>(null);
  const [filter, setFilter] = React.useState('');

  React.useEffect(() => {
    (async () => {
      try {
        const t = await getToken().catch(() => '');
        const j = await apiGet<{ candidates: BlindCandidate[] }>('/api/v1/client/marketplace/blind', t);
        setItems(j.candidates ?? []);
      } catch { setItems([]); }
    })();
  }, [getToken]);

  const filtered = (items ?? []).filter((c) =>
    !filter ||
    c.blind_id?.toLowerCase().includes(filter.toLowerCase()) ||
    c.title?.toLowerCase().includes(filter.toLowerCase()) ||
    (c.skills_top5 ?? []).some((s) => s.toLowerCase().includes(filter.toLowerCase()))
  );
  const green = (items ?? []).filter((c) => c.fairness_grade === 'GREEN').length;
  const avg = items && items.length ? Math.round(items.reduce((s, c) => s + (c.wolf_score ?? 0), 0) / items.length) : 0;

  return (
    <div>
      <PageTitle
        kicker="Talent marketplace"
        title="Browse anonymised candidates"
        right={<Pill tone="live"><ScanText className="h-3 w-3" /> wolf_brain_v3</Pill>}
      />

      <Section title="At a glance">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"  label="Anonymised candidates" value={fmt(items?.length)} />
          <Kpi accent="green" label="Fairness GREEN"        value={fmt(green)} />
          <Kpi              label="Avg Wolf score"        value={fmt(avg)} hint="0–100, deterministic" />
          <Kpi              label="PII exposure"          value="0" hint="wolf_id is the only handle" />
        </div>
      </Section>

      <Section
        title="Candidates"
        hint="Each card is a Wolf-ID. Click a fiche to open the printable, no-PII detail page."
      >
        <input
          placeholder="Filter by wolf-id / title / skill…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full mb-6 rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
        />
        {items === null && <Card>Loading…</Card>}
        {items && items.length === 0 && (
          <EmptyState
            title="Marketplace is empty for your tenant scope"
            hint="Run a mission match to populate the marketplace with ranked candidates."
          />
        )}
        {items && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map((c) => (
              <Card key={c.blind_id}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{c.blind_id}</p>
                    <p className="text-white text-sm mt-1">{c.title ?? '—'}</p>
                    <p className="text-[11px] text-white/50 font-mono mt-0.5">
                      {c.seniority ?? '—'}{c.years_experience ? ` · ${c.years_experience}y` : ''} · {c.city_zone ?? '—'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <ScoreChip score={c.wolf_score} />
                    <FairnessPill grade={c.fairness_grade} />
                  </div>
                </div>
                {c.skills_top5 && c.skills_top5.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {c.skills_top5.slice(0, 5).map((s) => (
                      <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 font-mono">{s}</span>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <Btn as="a" href={`${API_BASE}/api/v1/client/blind/${c.blind_id}/fiche.html`}>Open fiche</Btn>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
